/* R44-335 — PDP choices in the existing collection bottom sheet.
 * Native variant/model controllers own all commercial and gallery changes.
 * This controller presents those controls and resolves color links before travel.
 */
(() => {
  'use strict';
  if (window.BrilintonPdpSelector) return;
  const instances = new WeakMap();
  const norm = (value) => String(value ?? '').toLowerCase().replace(/[\s_-]+/g, '');
  const sizeKey = (value) => norm(value).replace(/mm$/, '');
  const parse = (value, fallback = null) => { try { return JSON.parse(value); } catch (_) { return fallback; } };
  const one = (root, selector) => root?.querySelector(selector);
  const all = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);
  const read = (root, selector) => one(root, selector)?.textContent?.trim() || '';
  const surfaceName = 'pdp-context-selector';
  let activeInstance = null;
  let instanceId = 0;

  // R44-339: gallery photos contain studio background; selector PNGs do not.
  // Measure only a small sample of a loaded color photo, keeping all non-background
  // pixels plus a safety margin. Textured scenes and unreadable images stay contained.
  const studioFits = new Map();
  function studioPhotoFit(img) {
    const ratio = img.naturalWidth / img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(180 * Math.min(1, ratio)));
    canvas.height = Math.max(1, Math.round(180 * Math.min(1, 1 / ratio)));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const width = canvas.width, height = canvas.height;
    const edges = [];
    for (let x = 0; x < width; x++) edges.push(x * 4, ((height - 1) * width + x) * 4);
    for (let y = 1; y < height - 1; y++) edges.push(y * width * 4, (y * width + width - 1) * 4);
    const transparent = edges.filter(i => data[i + 3] < 16).length >= edges.length * .95;
    const background = [0, 1, 2].map(channel => {
      const samples = edges.map(i => data[i + channel]).sort((a, b) => a - b);
      return samples[Math.floor(samples.length / 2)];
    });
    const isBackground = i => transparent ? data[i + 3] < 16 :
      data[i + 3] >= 240 && background.every((value, channel) => Math.abs(data[i + channel] - value) <= 8);
    if (edges.filter(isBackground).length < edges.length * .95) return null;
    let left = width, top = height, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (isBackground((y * width + x) * 4)) continue;
        left = Math.min(left, x); right = Math.max(right, x);
        top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
    }
    if (right < left || bottom < top) return null;
    // Very small marks are not enough evidence of a product silhouette.
    if ((right - left + 1) / width < .2 || (bottom - top + 1) / height < .2) return null;
    left = Math.max(0, left - 2); right = Math.min(width, right + 3);
    top = Math.max(0, top - 2); bottom = Math.min(height, bottom + 3);
    const containedWidth = Math.min(1, ratio / (8 / 9));
    const containedHeight = Math.min(1, (8 / 9) / ratio);
    const scale = Math.min(2, .96 / ((right - left) / width * containedWidth), .96 / ((bottom - top) / height * containedHeight));
    if (scale <= 1.04) return null;
    return {
      scale,
      x: -scale * ((left + right) / (2 * width) - .5) * containedWidth * 100,
      y: -scale * ((top + bottom) / (2 * height) - .5) * containedHeight * 100
    };
  }

  function fitStudioPhoto(img) {
    if (!img.isConnected || !img.naturalWidth || !img.naturalHeight) return;
    const key = img.currentSrc || img.src;
    if (!studioFits.has(key)) {
      let fit = null;
      try { fit = studioPhotoFit(img); } catch (_) { /* Contain if pixel access is unavailable. */ }
      if (studioFits.size >= 80) studioFits.delete(studioFits.keys().next().value);
      studioFits.set(key, fit);
    }
    const fit = studioFits.get(key);
    img.toggleAttribute('data-studio-fit', Boolean(fit));
    ['scale', 'x', 'y'].forEach(name => {
      const property = `--pdp-photo-${name}`;
      if (fit) img.style.setProperty(property, `${fit[name].toFixed(5)}${name === 'scale' ? '' : '%'}`);
      else img.style.removeProperty(property);
    });
  }

  class PdpContextSelector {
    constructor(product) {
      this.product = product;
      this.data = parse(one(product, '[data-pdp-selector-data]')?.textContent);
      const template = one(product, '[data-pdp-selector-template]');
      if (!this.data || !template) return;
      // Keep the uploaded iPhone artwork on one source-pixel scale. CDN thumbnail
      // dimensions alone cannot retain the relative sizes of separately cropped PNGs.
      const iphoneImages = this.data.devices.filter(device => device.kind === 'iphone' && device.image && device.imageWidth > 0 && device.imageHeight > 0);
      this.iphoneImageBounds = {
        width: Math.max(0, ...iphoneImages.map(device => device.imageWidth)),
        height: Math.max(0, ...iphoneImages.map(device => device.imageHeight))
      };
      this.shell = template.content.firstElementChild.cloneNode(true);
      document.body.append(this.shell);
      this.panel = one(this.shell, '[role="dialog"]');
      this.rail = one(this.shell, '[data-pdp-selector-options]');
      this.title = one(this.shell, '#PdpContextSelectorTitle');
      this.subtitle = one(this.shell, '#PdpContextSelectorSubtitle');
      this.panel.id = `PdpContextSelector-${++instanceId}`;
      this.title.id = `${this.panel.id}-title`;
      this.subtitle.id = `${this.panel.id}-subtitle`;
      this.panel.setAttribute('aria-labelledby', this.title.id);
      this.panel.setAttribute('aria-describedby', this.subtitle.id);
      this.notice = one(this.shell, '[data-pdp-selector-notice]');
      this.closeButton = one(this.panel, '[data-pdp-selector-close]');
      this.handlers = new AbortController();
      this.enhance();
      this.listen(product, 'click', (event) => this.onTrigger(event), true);
      this.listen(document, 'keydown', (event) => this.onKeydown(event));
      this.listen(document, 'focusin', (event) => {
        if (this.opened && !this.panel.contains(event.target) && window.BrilintonSurfaceManager?.top() === surfaceName) this.closeButton.focus({ preventScroll: true });
      });
      all(this.shell, '[data-pdp-selector-close]').forEach((button) => this.listen(button, 'click', () => this.close()));
      this.listen(window, 'resize', () => { if (this.opened) { this.measure(); this.align(); } });
      this.listen(window, 'pagehide', () => this.close(false));
      this.listen(window, 'pageshow', () => this.close(false));
      this.listen(window, 'brilinton:layout-refresh', (event) => {
        if (/resume|restore/.test(event.detail?.reason || '')) this.close(false);
      });
      this.listen(document, 'click', (event) => {
        if (this.opened && event.target.closest('header-drawer summary, #Details-menu-drawer-container > summary')) this.close(false);
      });
      this.bindDrag();
      this.observer = new MutationObserver(() => {
        this.enhance();
        if (this.opened && !this.row?.isConnected) this.close(false);
      });
      this.observer.observe(one(product, '[data-product-options-stack]') || product, { childList: true, subtree: true });
      instances.set(product, this);
    }

    listen(node, type, callback, capture = false) {
      node.addEventListener(type, callback, { capture, signal: this.handlers.signal });
    }

    rowKind(row) {
      if (!row || row.hidden || row.closest('[hidden], [aria-hidden="true"]')) return '';
      if (row.closest('[data-product-option-group="color"]')) return 'color';
      if (row.closest('variant-selects') && one(row, 'input[type="radio"], select')) return 'variant';
      if (one(row, '[data-royal-pop-model-choice], [data-interface-model-choice], [data-interface-device-choice], [data-bandware-apple-options], [data-bandware-royal-options]')) return 'model';
      return '';
    }

    enhance() {
      all(this.product, '[data-product-options-stack] details.variant-collapse').forEach((row) => {
        if (!this.rowKind(row) || row.hasAttribute('data-pdp-selector-enhanced')) return;
        row.dataset.pdpSelectorEnhanced = 'true';
        row.open = false;
        const summary = one(row, 'summary');
        summary?.setAttribute('aria-haspopup', 'dialog');
        summary?.setAttribute('aria-controls', this.panel.id);
        summary?.setAttribute('aria-expanded', 'false');
      });
    }

    onTrigger(event) {
      const summary = event.target.closest('summary');
      const row = summary?.parentElement;
      if (!this.product.contains(row) || !row?.matches('details.variant-collapse') || !this.rowKind(row)) return;
      if (!this.build(row)) return;
      event.preventDefault();
      event.stopPropagation();
      this.row = row;
      this.trigger = summary;
      row.open = false;
      this.open();
    }

    selectedOptions() {
      return (this.data.product.options || []).map((name, index) => {
        const row = one(this.product, `variant-selects [data-option-position="${index + 1}"]`);
        return { name, value: one(row, 'input[type="radio"]:checked, select')?.value || '' };
      });
    }

    nativePairing() { return Boolean(one(this.product, '[data-bandware-native-pdp], [data-interface-native-selector]')); }

    context() {
      // B¹ controllers own their device→technical variant and companion mapping.
      // Preserve their decorated links rather than treating 22/24mm as watch sizes.
      if (this.nativePairing()) return null;
      const params = new URL(location.href).searchParams;
      const memory = window.BrilintonDeviceContext;
      if (this.data.royal) {
        const value = params.get('model') || memory?.get('royalPop')?.modelSlug;
        if (!value) return null;
        const selected = all(this.product, '[data-royal-pop-model-choice]').find((card) => norm(card.dataset.modelSlug) === norm(value));
        return selected ? { kind: 'royal', value, label: selected.dataset.modelName } : null;
      }
      const isPhone = this.data.context === 'magware';
      const kind = isPhone ? 'iphone' : 'apple';
      const devices = this.data.devices.filter((device) => device.kind === kind);
      const remembered = memory?.get(isPhone ? 'iphone' : 'appleWatch');
      const requested = isPhone ? params.get('iphone_device') : params.get('size') || params.get('device');
      let device = devices.find((item) => isPhone ? norm(item.handle) === norm(requested) : sizeKey(item.value) === sizeKey(requested));
      // An explicit variant URL takes precedence over an older remembered device.
      if (!device && params.has('variant')) {
        const variant = this.data.product.variants.find((item) => String(item.id) === params.get('variant'));
        device = devices.find((item) => variant?.options.some((value) => norm(value) === norm(item.value)));
      }
      if (!device && remembered) device = devices.find((item) => isPhone ? norm(item.handle) === norm(remembered.handle) : sizeKey(item.value) === sizeKey(remembered.size));
      if (!device) return null;
      // Do not import an unrelated Apple Watch preference into another category.
      if (!isPhone && !['watchware', 'updated_template'].includes(this.data.context)) return null;
      return { ...device, label: isPhone ? `iPhone ${device.label}` : `${device.label} Apple Watch` };
    }

    colorStock(target, context) {
      if (!target?.variants?.length) return { state: 'unknown' };
      // A wholly sold-out color needs no device qualifier, even without size data.
      if (!target.variants.some((variant) => variant.available === true)) return { state: 'soldout', allSoldOut: true };
      let candidates = target.variants;
      const selected = this.selectedOptions();
      if (context && context.kind !== 'royal') {
        const sourceIndex = (this.data.product.options || []).findIndex((_name, index) =>
          this.data.product.variants.some((variant) => norm(variant.options[index]) === norm(context.value)));
        let targetIndex = (target.options || []).findIndex((name) => sourceIndex >= 0 && norm(name) === norm(this.data.product.options[sourceIndex]));
        if (targetIndex < 0) targetIndex = (target.options || []).findIndex((_name, index) => target.variants.some((variant) => norm(variant.options[index]) === norm(context.value)));
        if (targetIndex >= 0) {
          candidates = candidates.filter((variant) => norm(variant.options[targetIndex]) === norm(context.value));
          if (!candidates.length) return { state: 'unsupported' };
        } else if (context.kind === 'apple') {
          const specs = (target.specs || []).join(' ').toLowerCase();
          const sizes = Array.from(specs.matchAll(/\b(\d{2})\s*mm\b/g), (match) => match[1]);
          // If a destination has size variants, absence of this size is known.
          const hasSizes = target.variants.some((variant) => variant.options.some((value) => /^\d{2}\s*mm$/i.test(value)));
          if (hasSizes || (sizes.length && !sizes.includes(sizeKey(context.value)))) return { state: 'unsupported' };
          if (!sizes.length) return { state: 'unknown' };
        } else return { state: 'unsupported' };
      }
      selected.forEach(({ name, value }) => {
        if (!value || /colou?r/i.test(name)) return;
        const deviceValue = this.data.devices.some((device) => norm(device.value) === norm(value));
        if (deviceValue) return; // Explicit context above owns the requested device.
        const index = (target.options || []).findIndex((option) => norm(option) === norm(name));
        if (index >= 0) candidates = candidates.filter((variant) => norm(variant.options[index]) === norm(value));
      });
      if (!candidates.length) return { state: 'combination' };
      const variant = candidates.find((item) => item.available === true);
      return { state: variant ? 'available' : 'soldout', variant: variant || candidates[0] };
    }

    stockLabel(stock, context) {
      const suffix = context?.kind === 'apple' ? ` in ${context.value}` : context?.kind === 'iphone' ? ` for ${context.label}` : '';
      if (stock.state === 'unsupported') return `Not offered${suffix}`;
      if (stock.state === 'combination') return 'Not available with this selection';
      if (stock.state === 'soldout') return stock.allSoldOut ? 'Sold out' : `Sold out${suffix}`;
      if (stock.state === 'unknown') return context ? `Check availability${suffix}` : '';
      return '';
    }

    colorURL(source, stock, context) {
      const url = new URL(source.href, location.origin);
      const current = new URL(location.href);
      ['preview_theme_id', 'preview_key', '_fd', '_ab', 'pb'].forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      if (!this.nativePairing()) {
        const hasDeviceOption = this.data.product.variants.some((variant) => variant.options.some((value) => this.data.devices.some((device) => norm(device.value) === norm(value))));
        if (stock.variant && (context || !hasDeviceOption)) url.searchParams.set('variant', stock.variant.id);
        if (context?.kind === 'apple') url.searchParams.set('size', context.value);
        if (context?.kind === 'iphone') url.searchParams.set('iphone_device', context.handle);
        if (context?.kind === 'royal') url.searchParams.set('model', context.value);
      }
      return `${url.pathname}${url.search}${url.hash}`;
    }

    card({ label, meta = '', image = '', photo = false, device = null, selected = false, status = '', blocked = false, href = '', onChoose }) {
      const card = document.createElement(href && !blocked ? 'a' : 'button');
      if (card.tagName === 'BUTTON') card.type = 'button';
      else card.href = href;
      card.className = 'collection-context__option';
      if (device?.kind === 'apple') {
        card.classList.add('collection-context__option--apple');
        card.dataset.contextSize = `${sizeKey(device.value)}mm`;
      } else if (device?.kind === 'iphone') {
        card.classList.add('collection-context__option--iphone');
        if (device.imageWidth > 0 && device.imageHeight > 0 && this.iphoneImageBounds.width > 0 && this.iphoneImageBounds.height > 0) {
          card.style.setProperty('--iphone-image-width', `${100 * device.imageWidth / this.iphoneImageBounds.width}%`);
          card.style.setProperty('--iphone-image-height', `${100 * device.imageHeight / this.iphoneImageBounds.height}%`);
        }
      } else if (device?.kind === 'royal') {
        card.classList.add('collection-context__option--royal');
      }
      card.classList.toggle('is-selected', selected);
      if (selected) card.setAttribute('aria-current', 'true');
      if (blocked) card.setAttribute('aria-disabled', 'true');
      const media = document.createElement('span');
      media.setAttribute('aria-hidden', 'true');
      media.className = image ? 'collection-context__option-media' : 'pdp-context-selector__text-choice';
      if (image) {
        const img = document.createElement('img');
        if (photo) {
          let canFit = true;
          img.crossOrigin = 'anonymous';
          img.addEventListener('load', () => { if (canFit) fitStudioPhoto(img); });
          img.addEventListener('error', () => {
            // An image host without CORS must still be allowed to display normally.
            canFit = false;
            img.removeAttribute('data-studio-fit');
            img.removeAttribute('crossorigin');
          }, { once: true });
          queueMicrotask(() => { if (canFit && img.complete) fitStudioPhoto(img); });
        }
        if (typeof image === 'string') img.src = image;
        else {
          // Copy responsive sources, not the accordion's inline height or dimensions.
          ['src', 'srcset', 'sizes'].forEach((attribute) => {
            const value = image.getAttribute(attribute);
            if (value) img.setAttribute(attribute, value);
          });
        }
        img.className = 'collection-context__option-image';
        img.alt = '';
        img.loading = 'lazy';
        img.draggable = false;
        media.append(img);
      } else media.textContent = label;
      card.append(media);
      [['collection-context__option-title', label], ['collection-context__option-meta', meta], ['collection-context__option-meta pdp-context-selector__status', status]].forEach(([className, text]) => {
        if (!text) return;
        const span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        card.append(span);
      });
      card.addEventListener('click', (event) => {
        if (this.suppressRailClick) { event.preventDefault(); return; }
        // R44-341: the current choice only dismisses the sheet. Do not dispatch
        // another native change/model click or follow the current color's link.
        if (selected) { event.preventDefault(); this.close(); return; }
        if (blocked) { event.preventDefault(); return; }
        onChoose?.(event);
        this.close();
      });
      this.rail.append(card);
      return card;
    }

    build(row) {
      const kind = this.rowKind(row);
      const context = this.context();
      this.rail.replaceChildren();
      this.shell.dataset.kind = kind;
      this.notice.hidden = true;
      this.subtitle.textContent = '';
      if (kind === 'color') {
        this.title.textContent = 'Select color';
        this.subtitle.textContent = context ? `Colors for your ${context.label}` : (this.nativePairing() || this.data.royal) ? 'Choose your finish.' : 'Choose your size or model to see availability.';
        all(row, 'a.swatch').forEach((source) => {
          const target = parse(source.dataset.pdpColorProduct);
          const stock = this.nativePairing()
            ? { state: !target?.variants?.length ? 'unknown' : target.variants.some((variant) => variant.available) ? 'available' : 'soldout' }
            : this.colorStock(target, context);
          // Native B¹ sets have separate companion inventory. Never claim that an
          // entire configuration is available from the BandWare variant alone.
          const status = this.nativePairing() ? (stock.state === 'soldout' ? 'Sold out' : '') : this.stockLabel(stock, context);
          this.card({
            label: source.dataset.color || source.textContent.trim(), image: one(source, 'img'), photo: true,
            selected: source.classList.contains('active'), status,
            blocked: ['unsupported', 'soldout', 'combination'].includes(stock.state),
            href: this.colorURL(source, stock, context)
          });
        });
      } else if (kind === 'variant') {
        this.buildVariants(row);
      } else if (kind === 'model') {
        const appleModels = Boolean(one(row, '[data-interface-device-choice], [data-bandware-apple-options]'));
        this.title.textContent = appleModels ? 'Select your Apple Watch' : 'Select your Royal Pop model';
        this.subtitle.textContent = appleModels ? 'Select the case size printed on the back of your Apple Watch.' : 'Choose the Royal Pop model you own.';
        all(row, 'button.product-option-card').forEach((source) => {
          if (source.hidden || source.style.display === 'none') return;
          const deviceSize = source.dataset.deviceSize || source.dataset.bandwareAppleChoice;
          const device = deviceSize ? this.data.devices.find((item) => item.kind === 'apple' && sizeKey(item.value) === sizeKey(deviceSize)) : { kind: 'royal' };
          this.card({ label: read(source, '.product-option-card__label') || source.getAttribute('aria-label') || source.textContent.trim(),
            image: device?.image || one(source, 'img'), meta: read(source, '.product-option-card__meta'), device,
            selected: source.classList.contains('is-selected') || source.getAttribute('aria-pressed') === 'true',
            blocked: source.disabled || source.getAttribute('aria-disabled') === 'true',
            onChoose: () => source.click() });
        });
        const note = one(row, '.royal-pop-model-selector__notice, .b1-interface-native-selector__notice, .bandware-native-pdp__field-note');
        if (note) { this.notice.textContent = note.textContent.trim(); this.notice.hidden = false; }
      }
      return this.rail.children.length > 0;
    }

    buildVariants(row) {
      const name = row.dataset.optionName || read(row, 'summary > span');
      const position = Number(row.dataset.optionPosition) - 1;
      const selected = this.selectedOptions().map((option) => option.value);
      this.title.textContent = `Select ${String(name).replace(/:$/, '').toLowerCase()}`;
      const values = all(row, 'input[type="radio"], select option');
      values.forEach((source) => {
        const device = this.data.devices.find((item) => norm(item.value) === norm(source.value));
        const label = source.tagName === 'OPTION' ? source.parentElement : one(row, `label[for="${CSS.escape(source.id)}"]`);
        // Respect the existing geo restriction stylesheet as well as explicit hiding.
        if (label && getComputedStyle(label).display === 'none') return;
        if (device?.kind === 'apple') {
          this.title.textContent = 'Select your Apple Watch';
          this.subtitle.textContent = 'Select the case size printed on the back of your Apple Watch.';
        }
        if (device?.kind === 'iphone') {
          this.title.textContent = 'Select your iPhone';
          this.subtitle.textContent = 'Choose your exact Pro or Pro Max model.';
        }
        const matches = this.data.product.variants.filter((variant) => variant.options.every((value, index) => norm(value) === norm(index === position ? source.value : selected[index] || value)));
        const alternatives = this.data.product.variants.filter((variant) => norm(variant.options[position]) === norm(source.value));
        const availableWithOtherOptions = alternatives.some((variant) => variant.available);
        const soldout = alternatives.length > 0 && !availableWithOtherOptions;
        const unsupported = alternatives.length === 0;
        const needsOtherOption = availableWithOtherOptions && !matches.some((variant) => variant.available);
        this.card({ label: device?.label || source.value, meta: device?.meta || '', image: device?.image || '', device,
          selected: source.checked || source.selected, blocked: soldout || unsupported || source.disabled,
          status: unsupported ? 'Not available with this selection' : soldout ? 'Sold out' : needsOtherOption ? 'Choose another option to complete this selection' : '',
          onChoose: () => {
            if (source.tagName === 'OPTION') { source.parentElement.value = source.value; source.parentElement.dispatchEvent(new Event('change', { bubbles: true })); }
            else { source.checked = true; source.dispatchEvent(new Event('change', { bubbles: true })); }
          }
        });
      });
      all(row, '[data-b1-cross-product-size-route]').forEach((source) => {
        const device = this.data.devices.find((item) => item.kind === 'apple' && sizeKey(item.value) === sizeKey(source.dataset.b1TargetSize));
        const card = this.card({ label: source.dataset.b1TargetSize, image: device?.image || '', meta: device?.meta || '', device,
          href: this.colorURL(source, {}, null) });
        ['b1CrossProductSizeRoute', 'b1TargetProduct', 'b1TargetSize'].forEach((key) => { card.dataset[key] = source.dataset[key]; });
      });
    }

    measure() {
      let bottom = 0;
      all(document, '.section-header, sticky-header, .header-wrapper, .announcement-bar-section').forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0 && rect.top < 180 && rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
      });
      this.shell.style.setProperty('--collection-context-overlay-top', `${Math.floor(bottom)}px`);
    }

    align() {
      this.rail.classList.toggle('is-overflowing', this.rail.scrollWidth > this.rail.clientWidth + 1);
      const selected = one(this.rail, '.is-selected');
      this.rail.scrollLeft = selected ? Math.max(0, selected.offsetLeft - (this.rail.clientWidth - selected.offsetWidth) / 2) : 0;
    }

    open() {
      if (activeInstance && activeInstance !== this) activeInstance.close(false);
      activeInstance = this;
      this.measure();
      this.opened = true;
      this.panel.inert = false;
      this.panel.removeAttribute('inert');
      this.panel.setAttribute('aria-hidden', 'false');
      this.shell.classList.add('is-selector-sheet-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      const manager = window.BrilintonSurfaceManager;
      manager?.register(surfaceName, { root: this.panel, close: () => this.close(), topLevel: true });
      manager?.open(surfaceName, { trigger: this.trigger, lockMode: 'overflow' });
      if (!manager) { this.savedOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
      requestAnimationFrame(() => {
        if (!this.opened) return;
        this.align();
        (one(this.rail, '.is-selected:not([aria-disabled="true"])') || this.closeButton).focus({ preventScroll: true });
      });
    }

    close(restoreFocus = true) {
      if (!this.opened) return;
      this.opened = false;
      if (activeInstance === this) activeInstance = null;
      this.shell.classList.remove('is-selector-sheet-open');
      this.panel.style.removeProperty('--collection-context-sheet-drag-y');
      this.panel.classList.remove('is-dragging');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.inert = true;
      this.panel.setAttribute('inert', '');
      const manager = window.BrilintonSurfaceManager;
      if (manager?.isOpen(surfaceName)) manager.close(surfaceName, { restoreFocus });
      if (!manager) {
        document.body.style.overflow = this.savedOverflow || '';
        if (restoreFocus) this.trigger?.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (!this.opened) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = all(this.panel, 'button:not([disabled]), a[href]');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    bindDrag() {
      const handle = one(this.shell, '[data-pdp-selector-drag]');
      let sheetDrag = null;
      this.listen(handle, 'pointerdown', (event) => {
        if (event.button !== 0) return;
        sheetDrag = { id: event.pointerId, y: event.clientY, distance: 0 };
        handle.setPointerCapture?.(event.pointerId);
        this.panel.classList.add('is-dragging');
      });
      this.listen(handle, 'pointermove', (event) => {
        if (sheetDrag?.id !== event.pointerId) return;
        sheetDrag.distance = Math.max(0, event.clientY - sheetDrag.y);
        this.panel.style.setProperty('--collection-context-sheet-drag-y', `${sheetDrag.distance}px`);
      });
      const finishSheet = (event) => {
        if (sheetDrag?.id !== event.pointerId) return;
        const dismiss = event.type !== 'pointercancel' && sheetDrag.distance >= 64;
        sheetDrag = null;
        this.panel.classList.remove('is-dragging');
        this.panel.style.removeProperty('--collection-context-sheet-drag-y');
        if (dismiss) this.close();
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => this.listen(handle, type, finishSheet));
      let railDrag = null;
      this.listen(this.rail, 'pointerdown', (event) => {
        this.suppressRailClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        railDrag = { id: event.pointerId, x: event.clientX, left: this.rail.scrollLeft };
      });
      this.listen(this.rail, 'pointermove', (event) => {
        if (railDrag?.id !== event.pointerId) return;
        const distance = event.clientX - railDrag.x;
        if (Math.abs(distance) < 6 && !this.suppressRailClick) return;
        this.suppressRailClick = true;
        this.rail.setPointerCapture?.(event.pointerId);
        this.rail.classList.add('is-dragging');
        this.rail.scrollLeft = railDrag.left - distance;
        event.preventDefault();
      });
      const finishRail = () => { railDrag = null; this.rail.classList.remove('is-dragging'); setTimeout(() => { this.suppressRailClick = false; }, 0); };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => this.listen(this.rail, type, finishRail));
      this.listen(this.rail, 'dragstart', (event) => event.preventDefault());
    }

    destroy() {
      this.close(false);
      this.observer?.disconnect();
      this.handlers.abort();
      this.shell.remove();
      instances.delete(this.product);
    }
  }

  const init = () => all(document, 'product-info[id^="MainProduct"]').forEach((product) => {
    if (!instances.has(product) && one(product, '[data-pdp-selector-data]')) new PdpContextSelector(product);
  });
  document.addEventListener('shopify:section:unload', (event) => all(event.target, 'product-info').forEach((product) => instances.get(product)?.destroy()));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('product-info:loaded', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.BrilintonPdpSelector = { init, destroy: (product) => instances.get(product)?.destroy() };
})();
